import { Search } from "lucide-react";
import { useEffect, useRef } from "react";
import { z } from "zod";
import { Input } from "@/components/ui/input";
import { useArchViz } from "./ArchVizContext";
import { Breadcrumb } from "./Breadcrumb";

// Optional scale/constraint stats for the header pill (spec §3.2). Not a typed
// field on Meta — read defensively from the meta bag and narrow at the boundary.
// `.catch([])` means any absent/malformed value yields no pill rather than throwing.
const constraintsSchema = z
  .array(
    z.object({
      label: z.string().min(1),
      value: z.union([z.string(), z.number()]).transform(String),
    }),
  )
  .catch([]);

/**
 * The breadcrumb + search + constraint-stats cluster shared by every header.
 * Rendered as a fragment so the host row (TopBar on the local route, the
 * solution/version/shared page headers elsewhere) lays it out inline: the
 * breadcrumb hugs the left, the internal `flex-1` spacer pushes search and the
 * constraints pill to the right, ahead of any trailing header actions.
 *
 * Folding these controls into the single header row — instead of a second bar
 * below it — reclaims a full horizontal band for the canvas.
 */
export function VizHeaderControls() {
  const { state, dispatch } = useArchViz();
  const arch = state.architecture;
  const searchRef = useRef<HTMLInputElement | null>(null);

  // `/` focuses the search input.
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key !== "/" || e.metaKey || e.ctrlKey || e.altKey) return;
      const tgt = e.target as HTMLElement | null;
      if (tgt && /^(INPUT|TEXTAREA|SELECT)$/.test(tgt.tagName)) return;
      if (tgt?.isContentEditable) return;
      e.preventDefault();
      searchRef.current?.focus();
      searchRef.current?.select();
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, []);

  if (!arch) return null;

  const constraints = constraintsSchema.parse(arch.meta.constraints);

  return (
    <>
      <Breadcrumb />
      <div className="flex-1" />
      <div className="relative w-full max-w-[280px] max-md:hidden">
        <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 h-3.5 w-3.5 text-muted-foreground/70 pointer-events-none" />
        <Input
          ref={searchRef}
          type="search"
          value={state.searchQuery}
          onChange={(e) => dispatch({ type: "SET_SEARCH_QUERY", query: e.target.value })}
          onKeyDown={(e) => {
            if (e.key === "Escape") {
              dispatch({ type: "SET_SEARCH_QUERY", query: "" });
              (e.target as HTMLInputElement).blur();
            }
          }}
          placeholder="Search containers, externals, actors…"
          className="h-8 pl-7 pr-8 text-xs"
        />
        <kbd className="absolute right-2 top-1/2 -translate-y-1/2 text-[10px] font-mono text-muted-foreground/70 bg-muted/60 px-1.5 py-0.5 rounded pointer-events-none">
          /
        </kbd>
      </div>
      {constraints.length > 0 && (
        <div className="flex items-center gap-1.5 text-meta text-ink-subtle max-lg:hidden">
          <span className="h-1.5 w-1.5 rounded-pill bg-success" aria-hidden="true" />
          {constraints.map((c, i) => (
            <span key={c.label} className="flex items-center gap-1.5">
              {i > 0 && (
                <span className="text-ink-subtle/50" aria-hidden="true">
                  ·
                </span>
              )}
              <span>
                <span className="font-semibold text-ink-heading">{c.value}</span> {c.label}
              </span>
            </span>
          ))}
        </div>
      )}
    </>
  );
}
